import { Check } from 'lucide-react';
import { cn } from '@reflex/ui/lib/utils';
import { SESSION_SORT_OPTIONS, type SessionSort } from '@/components/agent/useSessionSort';

interface SessionSortPickerProps {
  value: SessionSort;
  onChange: (sort: SessionSort) => void;
  className?: string;
}

/**
 * Radio-card picker for the agent sidebar's session order. Each card pairs a
 * label with a short description, matching the visual-first selection pattern
 * of the navigation-style and color-theme pickers.
 */
export function SessionSortPicker({ value, onChange, className }: SessionSortPickerProps) {
  return (
    <div
      role="radiogroup"
      aria-label="Session order"
      data-testid="session-sort-picker"
      className={cn('grid grid-cols-1 gap-3 sm:grid-cols-2', className)}
    >
      {SESSION_SORT_OPTIONS.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(option.value)}
            data-testid={`session-sort-option-${option.value}`}
            className={cn(
              'group relative flex flex-col gap-1 rounded-lg border p-3 text-left transition-colors',
              selected
                ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                : 'border-border hover:border-muted-foreground/30 hover:bg-muted/50',
            )}
          >
            <div className="text-xs font-medium">{option.label}</div>
            <div className="text-xs leading-snug text-muted-foreground">{option.description}</div>
            {selected && (
              <div className="absolute right-1.5 top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <Check className="h-3 w-3" />
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}
